/* ============================================================
   VP SEARCH JS

   Drives snippets/vp-search-panel.liquid. The header search icon
   opens the sheet in place instead of navigating; results come
   from Shopify's predictive search JSON endpoint and are drawn
   here. The only navigation is submitting the form, which falls
   through to the normal /search page.

   Everything is built with createElement / textContent rather
   than innerHTML: titles and vendor names are merchant data and
   must never be parsed as markup.

   Wishlist hearts go through window.VPWishlist (assets/vp-wishlist.js),
   the same store the category grid and header dialog use, so a
   piece saved from search shows as saved everywhere else.
   ============================================================ */
(function () {
  var DEBOUNCE_MS = 220;
  var MIN_CHARS = 2;
  var RECENT_KEY = 'vp:recent-searches';
  var RECENT_MAX = 5;

  var ICON_FILL = '<path d="M17 15V7H9"/><path d="M17 7L7 17"/>';
  var ICON_HEART = '<path d="M16 26.5C9.2 21.6 5.5 17.4 5.5 12.6C5.5 9.2 8.1 6.8 11.2 6.8C13.2 6.8 15 7.8 16 9.5C17 7.8 18.8 6.8 20.8 6.8C23.9 6.8 26.5 9.2 26.5 12.6C26.5 17.4 22.8 21.6 16 26.5Z"/>';

  /* ---- small helpers ---- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function svg(pathMarkup, viewBox) {
    var wrap = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    wrap.setAttribute('viewBox', viewBox || '0 0 24 24');
    wrap.setAttribute('fill', 'none');
    wrap.setAttribute('stroke', 'currentColor');
    wrap.setAttribute('stroke-width', '1.3');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = pathMarkup; /* static markup defined above, never merchant data */
    return wrap;
  }

  function srOnly(text) {
    return el('span', 'sr-only', text);
  }

  /* Text with the typed term marked, assembled as nodes so titles stay inert. */
  function highlighted(text, term) {
    var frag = document.createDocumentFragment();
    var at = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
    if (at < 0) {
      frag.appendChild(document.createTextNode(text));
      return frag;
    }
    if (at > 0) frag.appendChild(document.createTextNode(text.slice(0, at)));
    frag.appendChild(el('mark', null, text.slice(at, at + term.length)));
    frag.appendChild(document.createTextNode(text.slice(at + term.length)));
    return frag;
  }

  /* Shopify's money_format placeholders, so merchant currency settings hold. */
  /* The predictive search API is inconsistent: it returns whole cents ("43500")
     on some shops and major units ("435.00") on others, so the decimal point is
     the only thing that tells them apart. That test has to run on the RAW value
     -- parseFloat("435.00") is the number 435, which stringifies to "435" with
     the point gone, and 435 then reads as cents and prints as $4.35. Callers
     must pass what the API gave them, never a parsed number. */
  function toAmount(raw) {
    var value = typeof raw === 'string' ? raw.replace(/[^\d.-]/g, '') : raw;
    var amount = parseFloat(value);
    if (isNaN(amount)) return NaN;
    return String(value).indexOf('.') === -1 ? amount / 100 : amount;
  }

  function formatMoney(cents, format) {
    var amount = toAmount(cents);
    if (isNaN(amount)) return '';

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      var parts = number.toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = (format || '${{amount}}').match(placeholder);
    var key = match ? match[1] : 'amount';
    var out;
    switch (key) {
      case 'amount_no_decimals':
        out = withDelimiters(amount, 0);
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(amount, 2, "'");
        break;
      case 'amount_no_decimals_with_space_separator':
        out = withDelimiters(amount, 0, ' ');
        break;
      case 'amount_with_space_separator':
        out = withDelimiters(amount, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        out = withDelimiters(amount, 2, ' ');
        break;
      default:
        out = withDelimiters(amount, 2);
    }
    return (format || '${{amount}}').replace(placeholder, out);
  }

  /* Ask the CDN for a thumbnail rather than the full-size original. */
  function sized(url, width) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/') === -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function readRecent() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string'; }) : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* private mode / storage disabled — recent searches are a nicety, not a requirement */
    }
  }

  /* ---- panel ---- */

  function initSearch(root) {
    var panel = (root || document).querySelector('[data-vp-search]');
    if (!panel || panel.dataset.vpInit) return;
    panel.dataset.vpInit = '1';

    var form = panel.querySelector('[data-vp-search-form]');
    var input = panel.querySelector('[data-vp-search-input]');
    var ghostTyped = panel.querySelector('[data-vp-ghost-typed]');
    var ghostHint = panel.querySelector('[data-vp-ghost-hint]');
    var clearBtn = panel.querySelector('[data-vp-search-clear]');
    var body = panel.querySelector('.vp-srch__body');
    var railCol = panel.querySelector('.vp-srch__rail');
    var mainCol = panel.querySelector('.vp-srch__main');
    var rail = panel.querySelector('[data-vp-search-rail]');
    var results = panel.querySelector('[data-vp-search-results]');
    var feature = panel.querySelector('[data-vp-search-feature]');
    var status = panel.querySelector('[data-vp-search-status]');
    var foot = panel.querySelector('[data-vp-search-foot]');
    var footTerm = panel.querySelector('[data-vp-search-all-term]');
    var recentBox = panel.querySelector('[data-vp-search-recent]');
    var recentList = panel.querySelector('[data-vp-search-recent-list]');
    var announce = panel.querySelector('[data-vp-search-announce]');

    var suggestUrl = panel.dataset.suggestUrl || '/search/suggest';
    var moneyFormat = panel.dataset.moneyFormat || '${{amount}}';
    var limit = parseInt(panel.dataset.limit, 10) || 6;
    var labels = {
      products: panel.dataset.labelProducts || 'Visual matches',
      suggestions: panel.dataset.labelSuggestions || 'Suggestions',
      collections: panel.dataset.labelCollections || 'Collections',
      pages: panel.dataset.labelPages || 'Pages',
      noResults: panel.dataset.labelNoResults || 'No results could be found.',
      soldOut: panel.dataset.labelSoldOut || 'Sold out'
    };

    var timer = null;
    var controller = null;
    var lastQuery = '';
    var topSuggestion = '';
    var items = [];
    var highlightIndex = -1;
    var lastFocus = null;
    var headerWatch = null;
    var exitTimer = null;
    var closeTimer = null;
    /* Tracked rather than read off the [hidden] attribute: the sheet keeps that
       attribute off for the whole fade-out, so during those 500ms a click on
       the icon read as "already open" and closed it again instead of
       reopening. */
    var opened = false;

    /* ---- open / close ---- */

    function headerEl() {
      return document.querySelector('[data-vp-header]');
    }

    /* The header is fixed and its height changes with the scroll state, so the
       sheet is positioned from the header's live bottom edge, not a constant. */
    function position() {
      var header = headerEl();
      var top = 140;
      if (header) top = Math.max(header.getBoundingClientRect().bottom, 8);
      panel.style.setProperty('--vp-srch-top', Math.round(top) + 'px');
    }

    /* The bar resizes underneath us — it sheds its .clear padding, goes
       compact, and shrinks again when a mega panel opens. Scroll and resize
       events miss all of that, and the sheet would detach from the bar. */
    function watchHeader() {
      var header = headerEl();
      if (!header || typeof ResizeObserver !== 'function') return;
      headerWatch = new ResizeObserver(position);
      headerWatch.observe(header);
    }

    function unwatchHeader() {
      if (headerWatch) headerWatch.disconnect();
      headerWatch = null;
    }

    function isOpen() {
      return opened;
    }

    function open() {
      if (opened) return;
      opened = true;
      /* Cancels a teardown still pending from a close mid-fade. */
      clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      position();
      panel.removeAttribute('hidden');
      document.body.classList.add('vp-locked');
      /* Turns the bar solid and lifts it over the sheet — see the search-open
         block at the end of vp-header.css. */
      var header = headerEl();
      if (header) {
        clearTimeout(exitTimer);
        header.classList.remove('search-exit');
        header.classList.add('search-open');
      }
      renderRecent();
      requestAnimationFrame(function () {
        panel.classList.add('is-in');
        input.focus();
        input.select();
      });
      addEventListener('resize', position);
      addEventListener('resize', syncStickyColumns);
      addEventListener('scroll', position, { passive: true });
      watchHeader();
    }

    function close() {
      if (!opened) return;
      opened = false;
      panel.classList.remove('is-in');
      removeEventListener('resize', position);
      removeEventListener('resize', syncStickyColumns);
      removeEventListener('scroll', position);
      unwatchHeader();
      if (controller) controller.abort();

      /* Released immediately, not with the sheet. Waiting for the 500ms fade
         and then running the bar's own transitions (.6s background, .5s
         wordmark, .4s nav and icons — durations tuned for scrolling) made
         going back to the transparent home-page bar feel like a long lag.
         search-exit shortens those for the handover only. */
      var header = headerEl();
      if (header) {
        header.classList.remove('search-open');
        header.classList.add('search-exit');
        clearTimeout(exitTimer);
        exitTimer = setTimeout(function () { header.classList.remove('search-exit'); }, 260);
      }

      closeTimer = setTimeout(function () {
        panel.setAttribute('hidden', '');
        document.body.classList.remove('vp-locked');
      }, 500);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    /* ---- shared pieces ---- */

    function setGhost(typed, hint) {
      ghostTyped.textContent = typed || '';
      ghostHint.textContent = hint || '';
    }

    /* One row of the rail: a link to the full results, plus a button that drops
       the term into the field instead, so it can be refined before committing. */
    function termRow(text, url, term) {
      var row = el('li', 'vp-srch__term');

      var link = el('a', 'vp-srch__term-link');
      link.href = url;
      link.appendChild(el('span', 'vp-srch__dot'));
      var label = el('span');
      label.appendChild(highlighted(text, term));
      link.appendChild(label);
      row.appendChild(link);

      var fill = el('button', 'vp-srch__fill');
      fill.type = 'button';
      fill.dataset.vpSearchTerm = text;
      fill.appendChild(srOnly(text));
      fill.appendChild(svg(ICON_FILL));
      row.appendChild(fill);

      return row;
    }

    function railGroup(labelText, rows) {
      var box = el('section', 'vp-srch__group');
      box.appendChild(el('h2', 'vp-srch__label', labelText));
      var list = el('ul', 'vp-srch__terms');
      rows.forEach(function (row) { list.appendChild(row); });
      box.appendChild(list);
      return box;
    }

    /* ---- wishlist heart ---- */

    function heartFor(product, priceText, imageSrc, displayName) {
      var button = el('button', 'vp-srch__heart');
      button.type = 'button';
      var id = String(product.id || product.handle || product.url);

      var item = {
        id: id,
        title: displayName || product.title || '',
        price: priceText,
        image: imageSrc,
        url: product.url || '#'
      };

      function sync() {
        var saved = !!(window.VPWishlist && window.VPWishlist.has(id));
        button.classList.toggle('saved', saved);
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        button.title = saved ? 'Saved' : 'Save';
      }

      button.appendChild(srOnly(displayName || product.title || ''));
      button.appendChild(svg(ICON_HEART, '0 0 32 32'));
      sync();

      button.addEventListener('click', function (e) {
        /* The card is covered by a stretched link; keep the heart out of it. */
        e.preventDefault();
        e.stopPropagation();
        if (!window.VPWishlist) return;
        button.classList.toggle('saved', window.VPWishlist.toggle(item));
        sync();
      });

      /* Saving the same piece elsewhere on the page must light this one up too. */
      document.addEventListener('wishlist:change', sync);
      return button;
    }

    /* ---- product card ---- */

    /* WHAT A TERM IS ALLOWED TO HAVE MATCHED.

       The endpoint matches everything it indexes, and two of those fields
       cannot earn their keep in this catalogue:

         vendor  is "Vincent Peach" on all 263 products, so it separates
                 nothing -- typing "Peach" matched the whole catalogue and
                 returned an arbitrary six.
         body    is the full description, the largest text surface and the one
                 a result row cannot explain. "cool-toned" sits in exactly one
                 description and in no title or tag, and search returned it.

       TITLE ONLY. A product is kept when the term turns up in its NAME and
       nowhere else -- not the category, not the tags. The tags were in this
       test until the gemstone audit showed what they are worth: `Has Gemstone`
       is Y on four rings in the sheet and not one of them carries a gemstone
       tag in Shopify, while `Sub:Gemstones` sits on twelve products that the
       tagging pass no longer writes. A field that incomplete decides results
       a shopper cannot account for, which is the thing this filter exists to
       stop. The name is the one field that is always right and always visible
       on the row.

       PREFIX, NOT SUBSTRING. "earring" has to reach "Earrings" while "ring"
       must not reach "Earrings", so each token has to start a word.

       ANY token, not every token. Requiring all of them would throw away
       "sterling silver bracelet" against "Ball Chain | Sterling Silver",
       which is a result the shopper wants and the server ranked for a reason.

       A SKU IS WAVED THROUGH. Searching KD-RNG-00002-14Y finds Lion Head Ring
       through a server-side variant match, and the payload returns an empty
       variants array for it -- there is nothing local to re-check, so a term
       shaped like a code skips the filter rather than being wrongly dropped.
       "cool-toned" has the hyphen but no digit, so it is still filtered. */
    var SKU_SHAPED = /[-_]/;

    function looksLikeCode(term) {
      return SKU_SHAPED.test(term) && /\d/.test(term);
    }

    /* Everything that is not a letter or a digit is a word boundary, on BOTH
       sides. Normalising only the haystack was a real bug: "KD-RNG" stayed one
       token while the SKU had become "kd rng 00001 14y", so it matched
       nothing. */
    var SEPARATORS = /[^a-z0-9]+/g;

    function normalise(s) {
      return ' ' + String(s || '').toLowerCase().replace(SEPARATORS, ' ').trim() + ' ';
    }

    function nameMatch(product, term) {
      /* Name, handle and SKU -- the three fields that identify a piece rather
         than describe it. The handle is the title with the awkwardness taken
         out, which is accent and apostrophe folding for nothing: "pave"
         reaches Pave in a Pod and "mens" reaches Kingdom Men's Signet Ring,
         neither of which the title spells that way. The SKUs arrive from
         vp-search-materials, since the payload's variants array is empty. */
      var extra = materials[product.handle];
      var hay = normalise([
        product.title || '',
        product.handle || '',
        extra && extra.skus ? extra.skus : ''
      ].join(' '));

      var tokens = normalise(term).trim().split(' ')
        .filter(function (s) { return s.length > 1; });
      if (!tokens.length) return true;

      var found = tokens.filter(function (tok) { return hay.indexOf(' ' + tok) !== -1; });

      /* ALL tokens for a code, ANY token for prose.
         A whole SKU is one identifier that happens to contain separators, so
         "KD-RNG-00002-14Y" must land on the one piece that carries it -- under
         ANY it matched every product sharing the "kd" fragment. Prose is the
         opposite: "sterling silver bracelet" should still reach Ball Chain |
         Sterling Silver, which the server ranked for a reason. A bare prefix
         like "KD-RNG" has no digit, so it reads as prose and opens up to every
         Kingdom ring, which is what a partial code should do. */
      if (looksLikeCode(term)) return found.length === tokens.length;
      if (found.length) return true;

      /* Nothing matched and we never received the SKU map -- the section fetch
         can fail -- so a code-shaped term is waved through rather than wrongly
         dropped. With the map present the SKUs above have already answered. */
      return !extra && looksLikeCode(term);
    }

    /* THE NAME, AND WHAT WAS TAKEN OFF IT.

       A Shopify title carries its qualifiers after a pipe -- "Churchill Downs
       Necklace | Half Diamond | Sterling Silver" -- and every card on the site
       shows only the stem. Search has to agree with them, or the same piece
       reads one way here and another on the page it links to.

       But the stem alone is not enough HERE, where five Churchill Downs
       necklaces land next to each other: truncated, they would be five
       identical rows. So nothing is thrown away -- the qualifiers move to the
       second line, behind the category:

           Churchill Downs Necklace
           Necklaces . Half Diamond . Sterling Silver

       A BARE SKU IS A LAST RESORT, not a rule. "Gold Smiley Face Pendant |
       GSFP-0001" tells a shopper nothing, so the code is dropped wherever a
       real qualifier exists to show instead. Where it is the ONLY thing
       separating two rows -- and four Smiley pendants are exactly that, same
       type, same single variant, two of them even the same price -- it is
       shown, because an unhelpful distinction still beats none. */
    var SKU_TAIL = /^[A-Z]{2,}[-\s]?\d{2,}$/i;

    function titleParts(raw) {
      var bits = String(raw || '').split('|').map(function (s) { return s.trim(); });
      bits = bits.filter(function (s) { return s.length; });
      var stem = bits.shift() || '';
      var spoken = bits.filter(function (b) { return !SKU_TAIL.test(b); });
      var quals = spoken.length ? spoken : bits;
      return {
        name: stem,
        quals: quals,
        /* One string, for alt text, the wishlist and screen readers -- none of
           which have a second line to put the qualifiers on. */
        full: quals.length ? stem + ' \u2014 ' + quals.join(', ') : stem
      };
    }

    function productCard(product, term) {
      var card = el('div', 'vp-srch__card');
      var name = titleParts(product.title);

      var src = (product.featured_image && product.featured_image.url) || product.image || '';
      var thumb = el('div', 'vp-srch__thumb');
      if (src) {
        var img = document.createElement('img');
        img.src = sized(src, 240);
        img.alt = (product.featured_image && product.featured_image.alt) || name.full;
        img.loading = 'lazy';
        img.width = 96;
        img.height = 96;
        thumb.appendChild(img);
      }
      card.appendChild(thumb);

      var info = el('div', 'vp-srch__info');
      if (product.vendor) info.appendChild(el('span', 'vp-srch__vendor', product.vendor));
      var title = el('span', 'vp-srch__title');
      title.appendChild(highlighted(name.name, term));
      info.appendChild(title);

      /* Second line: the category, then the materials -- the same line the
         product cards carry, composed by the same snippet, so a piece reads
         the same in search as on the page it links to.

         The qualifiers the title gave up are the FALLBACK, not the default:
         they are whatever a merchant typed after a pipe, while the material
         line is built from the variant's own options and spec fields. A piece
         with no materials recorded still gets them, so the row never loses
         what distinguished it. */
      var material = (materials[product.handle] || {}).material;
      var detail = [];
      if (product.type) detail.push(product.type);
      if (material) detail.push(material);
      else detail = detail.concat(name.quals);
      var variant = product.variants && product.variants[0];
      if (variant && variant.title && variant.title !== 'Default Title') detail.push(variant.title);
      if (detail.length) info.appendChild(el('span', 'vp-srch__meta', detail.join(' · ')));
      card.appendChild(info);

      var priceBox = el('div', 'vp-srch__price');
      var compareRaw = product.compare_at_price_max || product.compare_at_price || 0;
      var currentRaw = product.price_min != null ? product.price_min : product.price;
      var compare = toAmount(compareRaw);
      var current = toAmount(currentRaw);
      var priceText = isNaN(current) ? '' : formatMoney(currentRaw, moneyFormat);
      if (compare > current) priceBox.appendChild(el('span', 'vp-srch__was', formatMoney(compareRaw, moneyFormat)));
      if (priceText) priceBox.appendChild(el('span', null, priceText));
      if (product.available === false) priceBox.appendChild(el('span', 'vp-srch__out', labels.soldOut));
      card.appendChild(priceBox);

      /* A stretched link rather than wrapping the card, so the heart button is
         not nested inside an anchor. */
      var link = el('a', 'vp-srch__card-link');
      link.href = product.url;
      link.appendChild(srOnly(name.full));
      card.appendChild(link);

      card.appendChild(heartFor(product, priceText, src,
        material ? name.name + ' \u2014 ' + material : name.full));
      return card;
    }

    /* Adds type=product to a Shopify-built search URL, leaving one that
       already carries a type alone, and leaving non-search URLs (a collection
       or a page) untouched -- they are not searches and have no type to set. */
    function productScoped(url) {
      if (!url) return url;
      if (url.indexOf('/search') === -1) return url;
      if (/[?&]type=/.test(url)) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'type=product';
    }

    /* ---- states ---- */

    /* Arrow keys walk live suggestions first, then products — the order a
       shopper reads them in. The static trending and recent lists stay out of
       it while results are up, or every keystroke would bury the matches
       behind rows that have nothing to do with the query. */
    function collectItems() {
      var nodes = [].slice.call(rail.querySelectorAll('.vp-srch__term-link'))
        .concat([].slice.call(results.querySelectorAll('.vp-srch__card-link')));
      if (!nodes.length) nodes = [].slice.call(panel.querySelectorAll('.vp-srch__term-link'));
      items = nodes;
      highlightIndex = -1;
    }

    function showIdle() {
      rail.textContent = '';
      results.hidden = true;
      results.textContent = '';
      status.hidden = true;
      foot.hidden = true;
      if (feature) feature.hidden = false;
      setGhost('', '');
      topSuggestion = '';
      announce.textContent = '';
      collectItems();
    }

    function showStatus(message, term) {
      rail.textContent = '';
      results.hidden = true;
      results.textContent = '';
      if (feature) feature.hidden = true;
      status.hidden = false;
      status.textContent = message;
      announce.textContent = message;
      foot.hidden = false;
      footTerm.textContent = '“' + term + '”';
      collectItems();
    }

    function render(data, term) {
      var found = (data && data.resources && data.resources.results) || {};
      var queries = found.queries || [];
      var products = (found.products || []).filter(function (p) { return nameMatch(p, term); });
      var collections = found.collections || [];
      var pages = found.pages || [];

      if (!queries.length && !products.length && !collections.length && !pages.length) {
        showStatus(labels.noResults.replace('{{ terms }}', term).replace('{{terms}}', term), term);
        return;
      }

      /* Rail: live suggestions above the merchant's trending list.

         The suggestion URLs come back from Shopify UNSCOPED --
         "/search?q=pearl&_pos=1&_psq=pear..." with no type -- so following one
         ran a search across products, pages AND articles. The grid on that
         page renders products only while the masthead counts every result, so
         a term that also hits two pages read as "9 results" above six pieces.
         Every form on the site posts type=product; these links have to agree
         with them. */
      rail.textContent = '';
      if (queries.length) {
        rail.appendChild(railGroup(labels.suggestions, queries.slice(0, 5).map(function (q) {
          return termRow(q.text, productScoped(q.url), term);
        })));
      }
      if (collections.length) {
        rail.appendChild(railGroup(labels.collections, collections.slice(0, 4).map(function (c) {
          return termRow(c.title, c.url, term);
        })));
      }
      if (pages.length) {
        rail.appendChild(railGroup(labels.pages, pages.slice(0, 3).map(function (p) {
          return termRow(p.title, p.url, term);
        })));
      }

      /* Main: product cards. */
      results.textContent = '';
      if (products.length) {
        var box = el('section', 'vp-srch__group');
        box.appendChild(el('h2', 'vp-srch__label', labels.products));
        var list = el('div', 'vp-srch__cards');
        products.slice(0, limit).forEach(function (p) { list.appendChild(productCard(p, term)); });
        box.appendChild(list);
        results.appendChild(box);
      }

      results.hidden = !products.length;
      if (feature) feature.hidden = products.length > 0;
      status.hidden = true;
      foot.hidden = false;
      footTerm.textContent = '“' + term + '”';

      collectItems();

      var tally = [];
      if (products.length) tally.push(Math.min(products.length, limit) + ' ' + labels.products.toLowerCase());
      if (queries.length) tally.push(queries.slice(0, 5).length + ' ' + labels.suggestions.toLowerCase());
      if (collections.length) tally.push(collections.slice(0, 4).length + ' ' + labels.collections.toLowerCase());
      announce.textContent = tally.join(', ');

      /* Inline completion: only offer it when the top suggestion actually
         continues what has been typed, so the hint never contradicts it. */
      topSuggestion = '';
      var first = queries[0] && queries[0].text;
      if (first && first.toLowerCase().indexOf(term.toLowerCase()) === 0 && first.length > term.length) {
        topSuggestion = term + first.slice(term.length);
        setGhost(term, first.slice(term.length));
      } else {
        setGhost('', '');
      }

      /* After the cards are in the DOM; the sync defers a frame of its own. */
      syncStickyColumns();
    }

    /* handle -> material line, replaced on every search. Module-scoped
       because productCard() is called from render() and should not have the
       map threaded through two layers of signature to reach it. */
    var materials = {};

    /* The section returns [["handle","material","skus"], ...] -- rows, not an
       object, because Liquid cannot print a brace. Read with a regex rather
       than a DOM parse: it is one known tag of our own making, and parsing
       markup to reach a JSON string is more machinery than the job needs. */
    function parseMaterials(html) {
      var map = {};
      if (!html) return map;
      var match = /data-vp-search-materials[^>]*>([\s\S]*?)<\/script>/.exec(html);
      if (!match) return map;
      var pairs;
      try { pairs = JSON.parse(match[1]); } catch (e) { return map; }
      if (!pairs || !pairs.length) return map;
      pairs.forEach(function (pair) {
        if (!pair || !pair[0]) return;
        map[pair[0]] = { material: pair[1] || '', skus: pair[2] || '' };
      });
      return map;
    }

    /* HOW FAR THE SHORTER COLUMN MAY TRAVEL BEFORE IT PINS.

       The CSS explains which inset and why. The number it needs is
       panelHeight - columnHeight, negative, which is the column's own
       overflow: scroll that far and its last line is at the foot of the
       panel, and from there `top` holds it while the other column carries on.

       Only the SHORTER column is pinned. The taller one sets the scroll
       length and has to travel all of it; it also fills the grid row, so it
       has no room to shift inside its own area even if it were marked.

       Math.min(0, ...) covers a column that already fits the panel: there is
       no overflow to spend, so the offset is 0 and it pins from the start,
       which is right -- a column with nothing more to show should not scroll
       away and leave a hole. */
    function syncStickyColumns() {
      if (!body || !railCol || !mainCol) return;
      requestAnimationFrame(function () {
        railCol.classList.remove('is-pin');
        mainCol.classList.remove('is-pin');
        var view = body.clientHeight;
        var railH = railCol.getBoundingClientRect().height;
        var mainH = mainCol.getBoundingClientRect().height;
        if (!view || Math.abs(railH - mainH) < 2) return;
        var pinned = railH < mainH ? railCol : mainCol;
        var travel = Math.min(0, Math.round(view - Math.min(railH, mainH)));
        pinned.style.setProperty('--vp-pin-top', travel + 'px');
        pinned.classList.add('is-pin');
      });
    }

    /* ---- fetching ---- */

    function search(term) {
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;

      /* NO resources[options][fields] HERE, AND IT IS NOT AN OVERSIGHT.

         The documented way to choose which fields a term may match is that
         option, and on this shop it is ignored. Verified against the live
         store, not just the dev proxy: asking for fields=variants.sku ALONE
         still returns five clean title matches for "pendant", which is
         impossible if the option were being honoured. Re-adding it would read
         as though the narrowing were handled when it is not; the filtering
         happens in nameMatch() on the way back instead. */
      var url = suggestUrl +
        '.json?q=' + encodeURIComponent(term) +
        /* NO `collection`. The store carries 237 published collections and 228
           of them are vp-* chapter feeds -- one per chapter per page, built to
           fill the cluster sections. Their titles are chapter headings, not
           destination names, so 34 of them share a title with another: a
           search for "equestrian" offered "Equestrian" twice, pointing at
           vp-cavallo-elegante-equestrian and vp-equestrian-collection-
           equestrian, which read as a bug.

           Even the five real signature collections do not belong here. This
           theme sends a reader to /pages/equestrian-collection, not to
           /collections/equestrian-collection, so the collection URL is the
           wrong door. The pages rail already answers "equestrian" with the
           four pages a reader actually wants. */
        '&resources[type]=product,query,page' +
        '&resources[limit]=' + limit +
        /* limit_scope=each, because the default is ALL: one budget of `limit`
           shared across products, collections, queries and pages together.
           That made the panel behave backwards -- the broader the term, the
           fewer pieces it showed, because collections and suggestions ate the
           slots first. "equestrian" returned 2 products + 1 collection + 2
           queries + 1 page; "equestrian charm" 4 products + 2 queries; and
           "equestrian charm montana", which matches nothing but products,
           filled all six. Narrowing a search was adding results.

           With `each` the limit is per resource type, so the product list is
           six deep whatever else happens to match. */
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last';

      /* THE SECOND REQUEST IS FOR THE MATERIAL LINE.

         The JSON payload has no materials in it -- the line is composed by
         vp-variant-material from the variant's options and the product's
         spec.* metafields, which only Liquid can read. So the same query goes
         a second time through the Section Rendering API, which runs
         vp-search-materials server-side and hands back a handle -> material
         map for exactly these results.

         It rides the same AbortController, so a newer keystroke cancels both,
         and it is allowed to fail: a card without a material falls back to
         the qualifiers the title gave up, which is strictly better than
         holding the whole panel for it. */
      var materialUrl = suggestUrl +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product' +
        '&resources[limit]=' + limit +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&section_id=vp-search-materials';

      var opts = controller ? { signal: controller.signal } : undefined;

      panel.classList.add('is-loading');
      Promise.all([
        fetch(url, opts).then(function (response) {
          if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
          return response.json();
        }),
        fetch(materialUrl, opts)
          .then(function (response) { return response.ok ? response.text() : ''; })
          .then(parseMaterials)
          .catch(function () { return {}; })
      ])
        .then(function (both) {
          panel.classList.remove('is-loading');
          if (term !== input.value.trim()) return; /* a newer keystroke already won */
          materials = both[1];
          render(both[0], term);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          panel.classList.remove('is-loading');
          /* Nothing to show inline, but the form still submits to /search. */
          showStatus(labels.noResults.replace('{{ terms }}', term).replace('{{terms}}', term), term);
        });
    }

    function onInput() {
      var term = input.value.trim();
      clearBtn.hidden = term.length === 0;
      setGhost('', '');
      topSuggestion = '';

      clearTimeout(timer);
      if (term.length < MIN_CHARS) {
        lastQuery = '';
        showIdle();
        return;
      }
      if (term === lastQuery) return;
      lastQuery = term;
      timer = setTimeout(function () { search(term); }, DEBOUNCE_MS);
    }

    /* ---- recent searches ---- */

    function renderRecent() {
      var list = readRecent();
      recentList.textContent = '';
      if (!list.length) {
        recentBox.hidden = true;
        return;
      }
      recentBox.hidden = false;
      list.forEach(function (term) {
        recentList.appendChild(termRow(term, form.action + '?q=' + encodeURIComponent(term) + '&type=product', ''));
      });
    }

    function rememberTerm(term) {
      if (!term) return;
      var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      writeRecent(list);
    }

    /* ---- keyboard ---- */

    function boxOf(node) {
      return node.closest('.vp-srch__card, .vp-srch__term') || node;
    }

    function setHighlight(next) {
      if (highlightIndex > -1 && items[highlightIndex]) boxOf(items[highlightIndex]).classList.remove('is-hl');
      highlightIndex = next;
      if (highlightIndex > -1 && items[highlightIndex]) {
        var box = boxOf(items[highlightIndex]);
        box.classList.add('is-hl');
        box.scrollIntoView({ block: 'nearest' });
      }
    }

    function acceptGhost() {
      if (!topSuggestion) return false;
      input.value = topSuggestion;
      setGhost('', '');
      topSuggestion = '';
      onInput();
      return true;
    }

    input.addEventListener('input', onInput);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        if (!items.length) return;
        e.preventDefault();
        setHighlight(highlightIndex + 1 >= items.length ? 0 : highlightIndex + 1);
      } else if (e.key === 'ArrowUp') {
        if (!items.length) return;
        e.preventDefault();
        setHighlight(highlightIndex - 1 < 0 ? items.length - 1 : highlightIndex - 1);
      } else if (e.key === 'Tab' && topSuggestion && !e.shiftKey) {
        e.preventDefault();
        acceptGhost();
      } else if (e.key === 'ArrowRight' && topSuggestion && input.selectionStart === input.value.length) {
        e.preventDefault();
        acceptGhost();
      } else if (e.key === 'Enter') {
        if (highlightIndex > -1 && items[highlightIndex]) {
          e.preventDefault();
          rememberTerm(input.value.trim());
          items[highlightIndex].click();
        }
      } else if (e.key === 'Escape') {
        if (input.value) {
          e.preventDefault();
          input.value = '';
          onInput();
        } else {
          close();
        }
      }
    });

    /* The ghost cannot follow a scrolled-past-the-edge input, so drop it. */
    input.addEventListener('scroll', function () {
      if (input.scrollLeft > 0) setGhost('', '');
    });

    form.addEventListener('submit', function () {
      rememberTerm(input.value.trim());
    });

    clearBtn.addEventListener('click', function () {
      input.value = '';
      lastQuery = '';
      onInput();
      input.focus();
    });

    panel.querySelectorAll('[data-vp-search-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    var wipe = panel.querySelector('[data-vp-search-wipe]');
    if (wipe) {
      wipe.addEventListener('click', function () {
        writeRecent([]);
        renderRecent();
      });
    }

    /* Trending / suggestion rows carry a button that fills the field instead of
       navigating, so a shopper can refine the term first. */
    panel.addEventListener('click', function (e) {
      var fill = e.target.closest('[data-vp-search-term]');
      if (!fill) return;
      e.preventDefault();
      input.value = fill.dataset.vpSearchTerm;
      lastQuery = '';
      onInput();
      input.focus();
    });

    /* Any result link leaves the page, so record the term on the way out. */
    panel.addEventListener('click', function (e) {
      if (e.target.closest('.vp-srch__card-link, .vp-srch__term-link')) rememberTerm(input.value.trim());
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen() && document.activeElement !== input) close();
    });

    /* Another header icon -- wishlist, bag, sign-in -- opening its own
       surface takes over: the sheet closes rather than sitting underneath
       it, still open, for when that surface is dismissed. Capture phase, so
       it runs before the wishlist trigger stops the click travelling. The
       search icon itself is left to its own toggle above. */
    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      var inUtils = e.target.closest && e.target.closest('.vp-hd-utils');
      if (inUtils && !e.target.closest('[data-vp-search-open]')) close();
    }, true);

    /* ---- triggers ---- */
    document.querySelectorAll('[data-vp-search-open]').forEach(function (trigger) {
      if (trigger.dataset.vpSearchBound) return;
      trigger.dataset.vpSearchBound = '1';
      trigger.addEventListener('click', function (e) {
        e.preventDefault(); /* the href stays as the no-JS fallback */
        /* The sheet covers everything below the nav, so there is no backdrop
           left to click away on — the icon has to close it as well as open it. */
        if (isOpen()) close();
        else open();
      });
    });

    showIdle();
  }

  function boot() { initSearch(document); }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function (e) { initSearch(e.target); });
})();
